import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { matchesAccessModule } from "../utils/accessControl";
import {
  isOwnerAwaitingPropertyApproval,
  isOwnerPropertyPending,
  ownerRestrictedHomePath,
  PROPERTY_INFO_CHANGED_EVENT,
  readPropertyInfo,
} from "../utils/propertyAccess";
import {
  APARTMENT_LOGO_CHANGED_EVENT,
  DEFAULT_BRAND_LOGO,
  fetchActiveApartmentLogoUrl,
  getInitialBrandLogoUrl,
  resolveApartmentLogoAdminId,
} from "../services/apartmentLogo";

const Sidebar = () => {
  const location = useLocation();
  const [userRoleRaw, setUserRoleRaw] = useState(
    () => localStorage.getItem("role") || "",
  );
  const [accessJson, setAccessJson] = useState(() =>
    localStorage.getItem("access"),
  );
  const [propertyInfo, setPropertyInfo] = useState(() => readPropertyInfo());
  // Show cached apartment logo immediately (no flash of default while API loads)
  const [brandLogoUrl, setBrandLogoUrl] = useState(() =>
    getInitialBrandLogoUrl(),
  );
  const userRole = String(userRoleRaw || "")
    .toUpperCase()
    .replace(/[^A-Z]/g, "");
  const isSuperAdmin = userRole === "SUPERADMIN";
  const isAdmin = userRole === "ADMIN";

  // adminId from localStorage; if missing, userid
  const logoAdminId = useMemo(() => resolveApartmentLogoAdminId(), []);

  const accessList = useMemo(() => {
    if (accessJson == null || accessJson === "") return null;
    try {
      const p = JSON.parse(accessJson);
      return Array.isArray(p)
        ? p.map((x) => String(x).trim()).filter(Boolean)
        : null;
    } catch {
      return null;
    }
  }, [accessJson]);

  const loadBrandLogo = useCallback(async () => {
    if (!logoAdminId) {
      setBrandLogoUrl(getInitialBrandLogoUrl());
      return;
    }
    // Keep cached logo visible; refresh quietly from API
    const url = await fetchActiveApartmentLogoUrl(logoAdminId);
    setBrandLogoUrl(url || DEFAULT_BRAND_LOGO);
  }, [logoAdminId]);

  // Keep role in sync (covers login/logout without full reload)
  useEffect(() => {
    const syncRole = () => setUserRoleRaw(localStorage.getItem("role") || "");
    const syncAccess = () => setAccessJson(localStorage.getItem("access"));
    const syncPropertyInfo = () => setPropertyInfo(readPropertyInfo());
    syncRole();
    syncAccess();
    syncPropertyInfo();
    window.addEventListener("storage", syncRole);
    window.addEventListener("storage", syncAccess);
    window.addEventListener("storage", syncPropertyInfo);
    window.addEventListener(PROPERTY_INFO_CHANGED_EVENT, syncPropertyInfo);
    return () => {
      window.removeEventListener("storage", syncRole);
      window.removeEventListener("storage", syncAccess);
      window.removeEventListener("storage", syncPropertyInfo);
      window.removeEventListener(PROPERTY_INFO_CHANGED_EVENT, syncPropertyInfo);
    };
  }, []);

  useEffect(() => {
    setUserRoleRaw(localStorage.getItem("role") || "");
    setAccessJson(localStorage.getItem("access"));
    setPropertyInfo(readPropertyInfo());
  }, [location.pathname]);

  // Active apartment logo for brand (same API as Logo.js getAll status:active)
  useEffect(() => {
    loadBrandLogo();
  }, [loadBrandLogo, location.pathname]);

  useEffect(() => {
    const onLogoChanged = () => loadBrandLogo();
    window.addEventListener(APARTMENT_LOGO_CHANGED_EVENT, onLogoChanged);
    return () =>
      window.removeEventListener(APARTMENT_LOGO_CHANGED_EVENT, onLogoChanged);
  }, [loadBrandLogo]);

  /** Legacy sessions: no `access` key → keep previous role-based behaviour.
   * Empty `[]` means no modules from the list (staff still get core menus). */
  const allow = (...names) =>
    accessList == null || matchesAccessModule(accessList, ...names);

  // Admin / SuperAdmin always see core menu; others follow role `access` list
  const isStaff = isAdmin || isSuperAdmin;

  const ownerAwaitingApproval = isOwnerAwaitingPropertyApproval(
    userRole,
    propertyInfo,
  );
  const ownerPending = isOwnerPropertyPending(userRole, propertyInfo);
  const restrictedHome = ownerRestrictedHomePath(userRole, propertyInfo);

  // Always show Dashboard for any authenticated user (Layout only mounts when logged in)
  const showDashboard = !ownerAwaitingApproval;
  const showInvite =
    !ownerAwaitingApproval &&
    (allow("InviteUsers", "Invite", "Invite Users") ||
      isStaff ||
      userRole === "OWNER");
  const showCommunication =
    !ownerAwaitingApproval &&
    !isSuperAdmin &&
    (allow("Communication") ||
      isAdmin ||
      userRole === "OWNER" ||
      userRole === "TENANT");
  const showPayment =
    !ownerAwaitingApproval && !isSuperAdmin && (allow("Payment") || isAdmin);
  const showRole = !ownerAwaitingApproval && isSuperAdmin;
  const showAdminReport = !ownerAwaitingApproval && isSuperAdmin;
  const showTenantReport =
    !ownerAwaitingApproval &&
    !isSuperAdmin &&
    userRole !== "OWNER" &&
    (isAdmin || allow("TenantReport", "Tenant Report"));
  const showOwnerTenantReport = !ownerAwaitingApproval && userRole === "OWNER";
  const showCommunity =
    !ownerAwaitingApproval &&
    !isSuperAdmin &&
    (allow("Community") ||
      isAdmin ||
      userRole === "OWNER" ||
      userRole === "TENANT");
  const showDesignation =
    !ownerAwaitingApproval &&
    !isSuperAdmin &&
    (allow("Designation") || isAdmin);
  const showLogo = !ownerAwaitingApproval && isSuperAdmin && allow("Logo");
  const showDocumentManagement =
    !ownerAwaitingApproval &&
    !isSuperAdmin &&
    (isAdmin || userRole === "OWNER" || userRole === "TENANT" || allow("DocumentManagement"));
  const showEVoting =
    !ownerAwaitingApproval &&
    !isSuperAdmin &&
    (isAdmin ||
      userRole === "OWNER" ||
      userRole === "TENANT" ||
      allow("EVoting", "E-Voting", "E Voting"));
  const showPropertyDetails = userRole === "OWNER";
  const showPropertyApproval =
    !ownerAwaitingApproval &&
    !isSuperAdmin &&
    (isAdmin || allow("PropertyApproval", "Property Approval"));
  const showApproval = showPropertyApproval;
  const showMaintenanceDues =
    !ownerAwaitingApproval && (userRole === "OWNER" || userRole === "TENANT");
  const showMaintenanceCharges = !ownerAwaitingApproval && isAdmin;
  const showChargeTypes = !ownerAwaitingApproval && isSuperAdmin;
  const showPay =
    showMaintenanceDues || showMaintenanceCharges || showChargeTypes;

  const showSettings =
    showPayment ||
    showRole ||
    showAdminReport ||
    showTenantReport ||
    showDesignation ||
    showLogo;

  const isSettingsRoute = useMemo(
    () =>
      location.pathname.startsWith("/role") ||
      location.pathname === "/admin-report" ||
      location.pathname === "/tenant-report" ||
      location.pathname === "/payment" ||
      location.pathname.startsWith("/designation") ||
      location.pathname.startsWith("/logo"),
    [location.pathname],
  );
  const [settingsOpen, setSettingsOpen] = useState(isSettingsRoute);

  const isApprovalRoute = useMemo(
    () => location.pathname.startsWith("/property-approval"),
    [location.pathname],
  );
  const [approvalOpen, setApprovalOpen] = useState(isApprovalRoute);

  const isPayRoute = useMemo(
    () =>
      location.pathname.startsWith("/maintenance-dues") ||
      location.pathname.startsWith("/maintenance-charges") ||
      location.pathname.startsWith("/payment-charge-types"),
    [location.pathname],
  );
  const [payOpen, setPayOpen] = useState(isPayRoute);

  useEffect(() => {
    if (isPayRoute && showPay) setPayOpen(true);
    else if (!isPayRoute) setPayOpen(false);
  }, [isPayRoute, showPay]);

  // Open Settings only on Settings child routes; close when leaving them
  useEffect(() => {
    if (isSettingsRoute && showSettings) setSettingsOpen(true);
    else if (!isSettingsRoute) setSettingsOpen(false);
  }, [isSettingsRoute, showSettings]);

  useEffect(() => {
    if (isApprovalRoute && showApproval) setApprovalOpen(true);
    else if (!isApprovalRoute) setApprovalOpen(false);
  }, [isApprovalRoute, showApproval]);

  return (
    <aside className="main-sidebar sidebar-dark-primary elevation-4">
      {/* Brand Logo — constrained inside sidebar container */}
      <Link
        to={ownerAwaitingApproval ? restrictedHome : "/dashboard"}
        className="brand-link apartment-brand-link"
      >
        <span className="apartment-brand-logo-wrap">
          <img
            src={brandLogoUrl}
            alt="Apartment logo"
            className="apartment-brand-logo"
            onError={(e) => {
              e.currentTarget.onerror = null;
              e.currentTarget.src = DEFAULT_BRAND_LOGO;
            }}
          />
        </span>
      </Link>
      {/* Sidebar */}
      <div className="sidebar">
        {/* Sidebar Menu */}
        <nav className="mt-2">
          <ul
            className="nav nav-pills nav-sidebar flex-column"
            role="menu"
            data-accordion="false"
          >
            {showDashboard ? (
              <li className="nav-item">
                <NavLink
                  to="/dashboard"
                  end
                  className={({ isActive }) =>
                    `nav-link${isActive ? " active" : ""}`
                  }
                >
                  <svg
                    className="nav-icon"
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <rect x="3" y="3" width="8" height="8" rx="2" />
                    <rect x="13" y="3" width="8" height="8" rx="2" />
                    <rect x="3" y="13" width="8" height="8" rx="2" />
                    <rect x="13" y="13" width="8" height="8" rx="2" />
                  </svg>
                  <p>Dashboard</p>
                </NavLink>
              </li>
            ) : null}

            {showInvite ? (
              <li className="nav-item">
                <NavLink
                  to="/invite"
                  end
                  className={({ isActive }) =>
                    `nav-link${isActive ? " active" : ""}`
                  }
                >
                  <i className="nav-icon fa-solid fa-users" />
                  <p>Invite Users</p>
                </NavLink>
              </li>
            ) : null}

            {showCommunication ? (
              <li className="nav-item">
                <NavLink
                  to="/communication"
                  end
                  className={({ isActive }) =>
                    `nav-link${isActive ? " active" : ""}`
                  }
                >
                  <i className="nav-icon fa-solid fa-users" />
                  <p>Communication</p>
                </NavLink>
              </li>
            ) : null}

            {showOwnerTenantReport ? (
              <li className="nav-item">
                <NavLink
                  to="/tenant-report"
                  className={({ isActive }) =>
                    `nav-link${
                      isActive || location.pathname.startsWith("/tenant-report")
                        ? " active"
                        : ""
                    }`
                  }
                >
                  <i className="nav-icon fa-solid fa-users" />
                  <p>Tenant Report</p>
                </NavLink>
              </li>
            ) : null}

            {showCommunity ? (
              <li className="nav-item">
                <NavLink
                  to="/community"
                  className={({ isActive }) =>
                    `nav-link${
                      isActive || location.pathname.startsWith("/community")
                        ? " active"
                        : ""
                    }`
                  }
                >
                  <i className="nav-icon fa-solid fa-people-roof" />
                  <p>Executive Committee </p>
                </NavLink>
              </li>
            ) : null}

            {showEVoting ? (
              <li className="nav-item">
                <NavLink
                  to="/e-voting"
                  className={({ isActive }) =>
                    `nav-link${
                      isActive || location.pathname.startsWith("/e-voting") ? " active" : ""
                    }`
                  }
                >
                  <i className="nav-icon fas fa-poll" />
                  <p>E-Voting</p>
                </NavLink>
              </li>
            ) : null}

            {showApproval ? (
              <li className={`nav-item ${approvalOpen ? "menu-open" : ""}`}>
                <a
                  href="#approval"
                  className="nav-link"
                  role="button"
                  onClick={(e) => {
                    e.preventDefault();
                    setApprovalOpen((v) => !v);
                  }}
                >
                  <i className="nav-icon fa-solid fa-clipboard-check" />
                  <p>
                    Approval
                    <i className="right fas fa-angle-left" />
                  </p>
                </a>
                <ul className="nav nav-treeview">
                  {showPropertyApproval ? (
                    <li className="nav-item">
                      <NavLink
                        to="/property-approval"
                        end
                        className={({ isActive }) =>
                          `nav-link${isActive ? " active" : ""}`
                        }
                      >
                        <i
                          className="nav-icon fa-solid fa-house-circle-check"
                          aria-hidden="true"
                        />
                        <p>PropertyApproval</p>
                      </NavLink>
                    </li>
                  ) : null}
                </ul>
              </li>
            ) : null}

            {showPropertyDetails ? (
              <li className="nav-item">
                <NavLink
                  to={
                    ownerPending ? "/property-details-add" : "/property-details"
                  }
                  end
                  className={({ isActive }) =>
                    `nav-link${isActive ? " active" : ""}`
                  }
                >
                  <i className="nav-icon fa-solid fa-house" />
                  <p>Property Details</p>
                </NavLink>
              </li>
            ) : null}

            {showPay ? (
              <li className={`nav-item ${payOpen ? "menu-open" : ""}`}>
                <a
                  href="#pay"
                  className="nav-link"
                  role="button"
                  onClick={(e) => {
                    e.preventDefault();
                    setPayOpen((v) => !v);
                  }}
                >
                  <i className="nav-icon fa-solid fa-wallet" />
                  <p>
                    Pay
                    <i className="right fas fa-angle-left" />
                  </p>
                </a>
                <ul className="nav nav-treeview">
                  {showMaintenanceCharges ? (
                    <li className="nav-item">
                      <NavLink
                        to="/maintenance-charges"
                        end
                        className={({ isActive }) =>
                          `nav-link${isActive ? " active" : ""}`
                        }
                      >
                        <i
                          className="nav-icon fa-solid fa-file-invoice-dollar"
                          aria-hidden="true"
                        />
                        <p>Maintenance Charges</p>
                      </NavLink>
                    </li>
                  ) : null}

                  {showMaintenanceDues ? (
                    <li className="nav-item">
                      <NavLink
                        to="/maintenance-dues"
                        end
                        className={({ isActive }) =>
                          `nav-link${isActive ? " active" : ""}`
                        }
                      >
                        <i
                          className="nav-icon fa-solid fa-indian-rupee-sign"
                          aria-hidden="true"
                        />
                        <p>Maintenance Dues</p>
                      </NavLink>
                    </li>
                  ) : null}

                  {showChargeTypes ? (
                    <li className="nav-item">
                      <NavLink
                        to="/payment-charge-types"
                        end
                        className={({ isActive }) =>
                          `nav-link${isActive ? " active" : ""}`
                        }
                      >
                        <i
                          className="nav-icon fa-solid fa-tags"
                          aria-hidden="true"
                        />
                        <p>Charge Types</p>
                      </NavLink>
                    </li>
                  ) : null}
                </ul>
              </li>
            ) : null}

            {showDocumentManagement ? (
              <li className="nav-item">
                <NavLink
                  to="/document-management"
                  end
                  className={({ isActive }) =>
                    `nav-link${isActive ? " active" : ""}`
                  }
                >
                  <i className="nav-icon fa-solid fa-file-pdf" />
                  <p>Document Management</p>
                </NavLink>
              </li>
            ) : null}

            {showSettings ? (
              <li className={`nav-item ${settingsOpen ? "menu-open" : ""}`}>
                <a
                  href="#settings"
                  className="nav-link"
                  role="button"
                  onClick={(e) => {
                    e.preventDefault();
                    setSettingsOpen((v) => !v);
                  }}
                >
                  <i className="nav-icon fa-solid fa-gear" />
                  <p>
                    Settings
                    <i className="right fas fa-angle-left" />
                  </p>
                </a>
                <ul className="nav nav-treeview">
                  {showPayment ? (
                    <li className="nav-item">
                      <NavLink
                        to="/payment"
                        end
                        className={({ isActive }) =>
                          `nav-link${isActive ? " active" : ""}`
                        }
                      >
                        <i
                          className="nav-icon fa-solid fa-credit-card"
                          aria-hidden="true"
                        />
                        <p>Payment</p>
                      </NavLink>
                    </li>
                  ) : null}
                  {showRole ? (
                    <li className="nav-item">
                      <NavLink
                        to="/role"
                        end
                        className={({ isActive }) =>
                          `nav-link${isActive ? " active" : ""}`
                        }
                      >
                        <i
                          className="nav-icon fa-solid fa-user-shield"
                          aria-hidden="true"
                        />
                        <p>Role</p>
                      </NavLink>
                    </li>
                  ) : null}
                  {showAdminReport ? (
                    <li className="nav-item">
                      <NavLink
                        to="/admin-report"
                        end
                        className={({ isActive }) =>
                          `nav-link${isActive ? " active" : ""}`
                        }
                      >
                        <i
                          className="nav-icon fa-solid fa-file-chart-column"
                          aria-hidden="true"
                        />
                        <p>Admin Report</p>
                      </NavLink>
                    </li>
                  ) : null}
                  {showTenantReport ? (
                    <li className="nav-item">
                      <NavLink
                        to="/tenant-report"
                        end
                        className={({ isActive }) =>
                          `nav-link${isActive ? " active" : ""}`
                        }
                      >
                        <i
                          className="nav-icon fa-solid fa-users"
                          aria-hidden="true"
                        />
                        <p>Tenant Report</p>
                      </NavLink>
                    </li>
                  ) : null}
                  {showDesignation ? (
                    <li className="nav-item">
                      <NavLink
                        to="/designation"
                        end
                        className={({ isActive }) =>
                          `nav-link${isActive ? " active" : ""}`
                        }
                      >
                        <i
                          className="nav-icon fa-solid fa-id-badge"
                          aria-hidden="true"
                        />
                        <p>Designation</p>
                      </NavLink>
                    </li>
                  ) : null}
                  {showLogo ? (
                    <li className="nav-item">
                      <NavLink
                        to="/logo"
                        className={({ isActive }) =>
                          `nav-link${
                            isActive || location.pathname.startsWith("/logo")
                              ? " active"
                              : ""
                          }`
                        }
                      >
                        <i
                          className="nav-icon fa-solid fa-image"
                          aria-hidden="true"
                        />
                        <p>Logo</p>
                      </NavLink>
                    </li>
                  ) : null}
                </ul>
              </li>
            ) : null}
          </ul>
        </nav>
      </div>
    </aside>
  );
};

export default Sidebar;
